import type { FC, KeyboardEvent } from 'react'
import { useCallback, useMemo } from 'react'

import type { SxProps, Theme } from '@mui/material'
import { Box, Link, Typography } from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { Link as RouterLink, useNavigate } from 'react-router-dom'

import { useAuthActions, useAuthStatus } from '@/features/user'

import { HOME_CONTENT } from '../defaults'

export const QuickStartPathSection: FC = () => {
  const { isAuthenticated } = useAuthStatus()
  const { login } = useAuthActions()
  const navigate = useNavigate()

  const visibleSteps = useMemo<readonly StepConfig[]>(
    () => (isAuthenticated ? STEPS.filter((step) => step.action !== 'login') : STEPS),
    [isAuthenticated],
  )

  const handleLoginClick = useCallback((): void => {
    login().catch(() => {})
  }, [login])

  const handleStepNavigate = useCallback(
    (path: string): void => {
      navigate(path)
    },
    [navigate],
  )

  return (
    <Box component="section" aria-labelledby="quick-start-heading" sx={sectionContainerSx}>
      <Typography id="quick-start-heading" component="h2" sx={sectionTitleSx}>
        {HOME_CONTENT.quickStart.title}
      </Typography>
      <Typography sx={sectionSubtitleSx}>{HOME_CONTENT.quickStart.description}</Typography>

      <Box component="ol" role="list" sx={stepsContainerSx}>
        {visibleSteps.map((step, index) => (
          <StepEntry
            key={step.title}
            step={step}
            stepNumber={index + 1}
            isLast={index === visibleSteps.length - 1}
            onLoginClick={handleLoginClick}
            onNavigate={handleStepNavigate}
          />
        ))}
      </Box>

      <Link component={RouterLink} to="/docs" sx={docsLinkSx}>
        {HOME_CONTENT.docsLink}
      </Link>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Private components
// ---------------------------------------------------------------------------

const StepEntry = ({ step, stepNumber, isLast, onLoginClick, onNavigate }: StepEntryProps) => {
  const label = `Step ${stepNumber}: ${step.title}`

  const handleClick = (): void => {
    if (step.action === 'login') {
      onLoginClick()
    } else {
      onNavigate(step.action.path)
    }
  }

  return (
    <>
      <Box component="li" sx={stepWrapperSx}>
        <Typography
          component="h3"
          role={step.action === 'login' ? 'button' : 'link'}
          tabIndex={0}
          aria-label={label}
          onClick={handleClick}
          onKeyDown={handleEnterOrSpace(handleClick)}
          sx={stepLinkSx}
        >
          {step.title}
        </Typography>
        <Typography sx={timeTextSx}>{step.time}</Typography>
      </Box>
      {!isLast && <ArrowForwardIcon sx={arrowIconSx} aria-hidden="true" />}
    </>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const handleEnterOrSpace =
  (handler: () => void) =>
  (event: KeyboardEvent): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handler()
    }
  }

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface StepConfig {
  readonly title: string
  readonly time: string
  readonly action: 'login' | { readonly path: string }
}

interface StepEntryProps {
  readonly step: StepConfig
  readonly stepNumber: number
  readonly isLast: boolean
  readonly onLoginClick: () => void
  readonly onNavigate: (path: string) => void
}

// ---------------------------------------------------------------------------
// Step data
// ---------------------------------------------------------------------------

const { steps } = HOME_CONTENT.quickStart

const STEPS: readonly StepConfig[] = [
  {
    title: steps.signIn.title,
    time: steps.signIn.time,
    action: 'login',
  },
  {
    title: steps.onboard.title,
    time: steps.onboard.time,
    action: { path: '/docs/onboarding' },
  },
  {
    title: steps.install.title,
    time: steps.install.time,
    action: { path: '/docs/daemon-setup' },
  },
  {
    title: steps.launch.title,
    time: steps.launch.time,
    action: { path: '/docs/daemon-usage#cmd-session-start' },
  },
]

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const sectionContainerSx: SxProps<Theme> = (theme) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  py: { xs: 4, md: 6 },
  px: { xs: 2, md: 4 },
  backgroundColor:
    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(62, 39, 35, 0.02)',
  borderTop: '1px solid',
  borderBottom: '1px solid',
  borderColor: 'divider',
})

const sectionTitleSx: SxProps<Theme> = {
  fontSize: { xs: '1.5rem', md: '1.75rem' },
  fontWeight: 600,
  color: 'text.primary',
  textAlign: 'center',
  mb: 1,
}

const sectionSubtitleSx: SxProps<Theme> = {
  fontSize: { xs: '1rem', md: '1.1rem' },
  fontWeight: 400,
  color: 'text.secondary',
  textAlign: 'center',
  mb: { xs: 4, md: 6 },
}

const stepsContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  alignItems: 'center',
  justifyContent: 'center',
  gap: { xs: 3, md: 2 },
  width: '100%',
  maxWidth: 960,
  listStyle: 'none',
  margin: 0,
  padding: 0,
}

const stepWrapperSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  flex: { xs: '1 1 100%', md: '1 1 0' },
  width: { xs: '100%', md: 'auto' },
}

const stepLinkSx: SxProps<Theme> = {
  fontSize: '1.1rem',
  fontWeight: 600,
  color: 'text.primary',
  cursor: 'pointer',
  textAlign: 'center',
  '@media (prefers-reduced-motion: no-preference)': {
    transition: 'color 0.2s ease-in-out',
  },
  '&:hover': {
    color: 'primary.main',
  },
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: '4px',
    borderRadius: 1,
  },
}

const timeTextSx: SxProps<Theme> = {
  textAlign: 'center',
  fontSize: '0.8rem',
  color: 'text.secondary',
  mt: 0.75,
}

const docsLinkSx: SxProps<Theme> = {
  mt: 4,
  fontSize: '0.9rem',
  color: 'text.secondary',
  textDecoration: 'none',
  '&:hover': { color: 'primary.main' },
}

const arrowIconSx: SxProps<Theme> = {
  display: { xs: 'none', md: 'flex' },
  color: 'text.disabled',
  fontSize: '1.5rem',
  flexShrink: 0,
}
